import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import type {DateRange} from "../../common/newDatePicker/datePickerShared";
import {GuestsBySpendCard, RevenueConcentrationCard} from "./crm-demographics/GuestValueCards";
import MostReservationsTable from "./tables/MostReservationsTable";
import TopSpendersTable from "./tables/TopSpendersTable";
import {insightsLoadingOf} from "../kit/insightsLoading";
import {useInsightsV2GuestValue} from "./useInsightsV2Stats";

const CARD_TITLES = ["Revenue concentration", "Guests by spend", "Top spenders", "Most reservations"];

interface CrmGuestValueSectionsProps {
    range: DateRange;
    compareRange: DateRange | null;
    matches?: (title: string, key: string) => boolean;
}

/**
 * Lifetime activity and value for the guests arriving in the selected range. Money views deliberately stay in
 * one section; the backend reuses the same folio totals for both charts and the Top spenders ranking.
 */
export function CrmGuestValueSections({range, compareRange, matches}: CrmGuestValueSectionsProps) {
    const {t} = useTranslation();
    const query = useInsightsV2GuestValue(range, compareRange);
    const queryLoading = insightsLoadingOf(query.isLoading, compareRange != null && query.isPreviousData);

    const shows = (title: string) => matches == null || matches(t(title), title);

    if (!CARD_TITLES.some(shows)) {
        return null;
    }

    return (
        <Stack gap={1}>
            <Typography variant="body1" color="text.secondary">
                {t("Spend & value")}
            </Typography>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "minmax(0, 1fr)", xl: "repeat(2, minmax(0, 1fr))"},
                    gap: 2,
                    minWidth: 0,
                }}
            >
                {shows("Revenue concentration") && (
                <RevenueConcentrationCard
                    items={query.data?.revenueConcentration ?? []}
                    loading={queryLoading}
                    comparing={Boolean(compareRange)}
                />
                )}
                {shows("Guests by spend") && (
                <GuestsBySpendCard
                    items={query.data?.guestsBySpend ?? []}
                    loading={queryLoading}
                    comparing={Boolean(compareRange)}
                />
                )}
                {shows("Top spenders") && (
                <TopSpendersTable
                    rows={query.data?.topSpenders ?? []}
                    comparisonRows={compareRange ? query.data?.compareTopSpenders ?? [] : undefined}
                    loading={queryLoading}
                />
                )}
                {shows("Most reservations") && (
                <MostReservationsTable
                    rows={query.data?.mostReservations ?? []}
                    comparisonRows={compareRange ? query.data?.compareMostReservations ?? [] : undefined}
                    loading={queryLoading}
                />
                )}
            </Box>
        </Stack>
    );
}

export default CrmGuestValueSections;
